import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { CheckCircle2, UploadCloud } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ALLOWED_EXTENSIONS = ['pdf'] as const;

interface FileDropZoneProps {
  file: File | null;
  onFile: (file: File | null) => void;
}

/**
 * Document ingestion area — click or drag-drop a PDF/DOCX, ≤ 25 MB.
 *
 * Owns its own drag state + validation; surfaces only the chosen
 * `File` to the parent. The accepted-extensions and size cap are
 * defined as module-level constants so the success/error copy can
 * reference them without prop threading.
 */
export function FileDropZone({ file, onFile }: FileDropZoneProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (candidate: File): string | null => {
    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      return t('ORGCONTRACTCHECKER_ERR_FILESIZE', {
        defaultValue: 'File is larger than 25MB.',
      });
    }
    const ext = candidate.name.toLowerCase().split('.').pop() ?? '';
    if (!ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number])) {
      return t('ORGCONTRACTCHECKER_ERR_FILETYPE', {
        defaultValue: 'Only PDF files are supported.',
      });
    }
    return null;
  };

  const accept = (candidate: File) => {
    const message = validate(candidate);
    if (message) {
      setError(message);
      onFile(null);
      return;
    }
    setError(null);
    onFile(candidate);
  };

  const openPicker = () => inputRef.current?.click();

  const reset = (event: React.MouseEvent) => {
    event.stopPropagation();
    setError(null);
    onFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="md:col-span-7 flex flex-col">
      <span className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-globe-grey shrink-0">
        {t('ORGCONTRACTCHECKER_LABEL_INGESTION', { defaultValue: 'Document ingestion' })}
      </span>

      <div
        role="button"
        tabIndex={0}
        aria-label={t('ORGCONTRACTCHECKER_UPLOAD_ARIA', { defaultValue: 'Upload contract' })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPicker();
          }
        }}
        onClick={openPicker}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          const dropped = event.dataTransfer.files?.[0];
          if (dropped) accept(dropped);
        }}
        className={cn(
          'relative group cursor-pointer border-2 border-dashed rounded-xl p-6 sm:p-10 flex flex-col items-center justify-center transition-all duration-200 min-h-56 sm:min-h-64 flex-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue/40',
          isDragging
            ? 'border-selise-blue bg-selise-blue/5'
            : 'border-border hover:border-selise-blue hover:bg-muted/50'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          className="sr-only"
          onChange={(event) => {
            const picked = event.target.files?.[0];
            if (picked) accept(picked);
          }}
        />

        {file ? (
          <div className="text-center w-full px-2">
            <CheckCircle2 className="w-12 h-12 text-poly-green mb-3 sm:mb-4 mx-auto" />
            <p className="text-sm font-bold text-[hsl(var(--fg))] truncate w-full">{file.name}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={reset}
              className="mt-2 h-auto px-2 py-1 text-xs font-semibold text-selise-blue hover:bg-selise-blue/5 hover:text-selise-blue"
            >
              {t('ORGCONTRACTCHECKER_REPLACE_FILE', { defaultValue: 'Replace file' })}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <UploadCloud className="w-12 h-12 text-selise-blue mb-3 sm:mb-4 mx-auto" />
            <p className="text-sm font-semibold mb-1 text-[hsl(var(--fg))]">
              {t('ORGCONTRACTCHECKER_UPLOAD_PROMPT', { defaultValue: 'Drag & drop contract' })}
            </p>
            <p className="text-xs text-globe-grey">
              {t('ORGCONTRACTCHECKER_UPLOAD_HINT', { defaultValue: 'PDF (Max 25MB)' })}
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 text-xs text-destructive shrink-0" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default FileDropZone;
